import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, FileText, Download, BarChart3, Filter } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';

const CATEGORIES = ['All', 'Analyst Report', 'Research', 'Whitepaper', 'Benchmark'];

const REPORTS = [
  {
    id: 1,
    category: 'Analyst Report',
    title: 'The State of Autonomous Operations 2026',
    excerpt: 'How leading enterprises are moving from reactive monitoring to AI-driven self-healing operations across hybrid estates.',
    pages: 42,
    date: 'Feb 2026',
    cover: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=70',
  },
  {
    id: 2,
    category: 'Research',
    title: 'Operational Intelligence Maturity Index',
    excerpt: 'A benchmark study of 500+ enterprises measuring observability, automation, and AI agent adoption.',
    pages: 28,
    date: 'Jan 2026',
    cover: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=70',
  },
  {
    id: 3,
    category: 'Whitepaper',
    title: 'Designing the AI-Native Control Plane',
    excerpt: 'Reference architecture for orchestrating agents, telemetry, and remediation workflows at planet scale.',
    pages: 36,
    date: 'Jan 2026',
    cover: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&q=70',
  },
  {
    id: 4,
    category: 'Benchmark',
    title: 'MTTR Benchmarks Across BFSI & Telco',
    excerpt: 'Cross-industry view on mean-time-to-resolve trends after AI agents are introduced into the SRE stack.',
    pages: 18,
    date: 'Dec 2025',
    cover: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&q=70',
  },
  {
    id: 5,
    category: 'Research',
    title: 'Context Model: From Signals to Decisions',
    excerpt: 'Why a unified context graph outperforms siloed dashboards for incident triage and capacity planning.',
    pages: 24,
    date: 'Dec 2025',
    cover: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=900&q=70',
  },
  {
    id: 6,
    category: 'Whitepaper',
    title: 'Securing Agent-Led Remediation',
    excerpt: 'A policy and audit framework for autonomous remediation in regulated industries.',
    pages: 31,
    date: 'Nov 2025',
    cover: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=900&q=70',
  },
];

const STATS = [
  { k: '85+', v: 'Original research papers' },
  { k: '12k+', v: 'Operations leaders served' },
  { k: '40+', v: 'Benchmarks published' },
  { k: '24', v: 'Industry verticals covered' },
];

const Insights = () => {
  const { open: openLead } = useLeadDialog();
  const [filter, setFilter] = useState('All');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const featured = REPORTS[0];
  const list = REPORTS.filter((r) => filter === 'All' || r.category === filter).slice(filter === 'All' ? 1 : 0);

  return (
    <div className="bg-black min-h-screen" data-testid="insights-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 20%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Resources</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Insights &amp; Reports</span>
            </nav>
            <div className="mt-7 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                <BarChart3 className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">Research library</span>
              </div>
              <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                Insights &amp; <span className="italic font-light brand-gradient-text">Reports.</span>
              </h1>
              <p className="mt-5 text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                Original research, analyst-grade reports, and benchmarks on observability, AI agents, and autonomous operations — written by the MetaxuOne research team and industry collaborators.
              </p>
            </div>

            {/* Stats strip */}
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {STATS.map((s) => (
                <div key={s.v} className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
                  <div className="text-[28px] sm:text-[32px] font-semibold text-white tracking-tight">{s.k}</div>
                  <div className="mt-1 text-[12.5px] text-neutral-400">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURED */}
        <section className="relative bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Featured report</p>
            <div className="mt-6 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-neutral-800">
                <img src={featured.cover} alt={featured.title} className="w-full h-[320px] lg:h-[420px] object-cover" />
                <div className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                  {featured.category}
                </div>
              </div>
              <div className="lg:col-span-6">
                <h2 className="text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  {featured.title}
                </h2>
                <p className="mt-5 text-[16px] text-neutral-300 leading-relaxed max-w-xl">
                  {featured.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-4 text-[12.5px] text-neutral-400">
                  <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {featured.pages} pages</span>
                  <span>•</span>
                  <span>{featured.date}</span>
                </div>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button
                    onClick={() => openLead({ source: `insight-${featured.id}`, title: `Download · ${featured.title}` })}
                    className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                    data-testid="featured-report-download-btn"
                  >
                    Download report <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => openLead({ source: 'insight-discuss', title: 'Discuss findings with us' })}
                    variant="ghost"
                    className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium"
                  >
                    Discuss findings
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LIBRARY */}
        <section className="relative bg-black border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">All research</p>
                <h2 className="mt-2 text-[26px] sm:text-[32px] lg:text-[40px] tracking-[-0.02em] font-semibold text-white">Library</h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-neutral-500" />
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    data-testid={`insights-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`text-[12.5px] px-3 py-1.5 rounded-full border transition-colors ${
                      filter === c
                        ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                        : 'border-neutral-800 text-neutral-300 hover:border-neutral-600'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((r) => (
                <article key={r.id} className="group rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden hover:border-neutral-600 transition-colors">
                  <div className="relative h-40 overflow-hidden">
                    <img src={r.cover} alt={r.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    <div className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                      {r.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-[16.5px] font-semibold text-white leading-snug">{r.title}</h3>
                    <p className="mt-2 text-[13.5px] text-neutral-400 leading-snug line-clamp-2">{r.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-[12px] text-neutral-500">
                      <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {r.pages} pages · {r.date}</span>
                      <button
                        onClick={() => openLead({ source: `insight-card-${r.id}`, title: `Download · ${r.title}` })}
                        className="inline-flex items-center gap-1 text-[12.5px] text-blue-300 hover:text-blue-200"
                        data-testid={`insights-card-download-${r.id}`}
                      >
                        Download <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-neutral-950">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h3 className="text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  Want research <span className="italic font-light brand-gradient-text">tailored to your stack?</span>
                </h3>
                <p className="mt-4 text-[15.5px] text-neutral-300 max-w-xl">
                  Our research team partners with leaders to publish private benchmarks, executive briefings and scenario models — built on your telemetry, not generic samples.
                </p>
              </div>
              <div className="lg:col-span-5 flex lg:justify-end">
                <Button
                  onClick={() => openLead({ source: 'insights-cta', title: 'Commission custom research' })}
                  className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                  data-testid="insights-cta-btn"
                >
                  Commission research <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Insights;
